import {ZIDX} from "../standaloneLibrary.js";
import {MarkdownStreamParser} from "../../ai/MarkdownStreamParser.js";
import {buildLeadCompleteCard, LeadCompletePayload} from "./LeadCompleteCard.js";
import jQuery from "jquery";
const $ = jQuery;

const AI_PROMPT_STORAGE_KEY = "zidx_ai_prompt_count";
const AI_PROMPT_EXPIRY_DAYS = 30;
const LAST_PROMPT_COOKIE_KEY = "zidx_last_ai_prompt";

interface ViewRecord {
    count: number;
    firstSeen: number; // unix ms
}

export class AIChatBoxConfig{
    aiEndPointURL="/ai-search/";
    aiBotId=0;
    extraQueryParams:string="";
    onPromptCallback:Function=function(prompt:string):string{ return prompt;};
    onText:Function=function(data:string):string{ return data;};
    onJSON:Function=function(data:any){};
    onXML:Function=function(data:string){};
    onTokens:Function=function(count:number){};
    onDataCompleteCallback:Function=function(){};
    initialSessionId:string|null=null;
    /** true: ignore ?prompt= and the saved prompt cookie (the page sends its own first message with sendPrompt) */
    suppressUrlPrompt:boolean=false;
    initialContactId:string|null=null;
    onSessionReady:Function=function(sessionId:string){};
    onHistoryLoaded: Function = function(messages: any[]) {};
    placeholder="Ask something..."; // initial prompt box placeholder
    clearPlaceholderAfterFirstReply=false; // custom skins blank the placeholder after the first prompt
    overviewCollapse=true; // false: always show the whole AI Overview, no Read more button
    overviewLabel=""; // non-empty turns each answer into a collapsible "AI Overview" block with this heading
    onSearchQuery:Function=function(query:string){}; // the query the server searched for this prompt (search results feature)
    /** non-null shows a "Start a new chat" button on the thank-you card */
    onNewChat:(()=>void)|null=null;
    /** true: show the thank-you card after the history loads (page URL had thanks=1) */
    initialThanks=false;
    onThanksShown:Function=function(){};
    onThanksHidden:Function=function(){};
    /** the server asks the page to go to the listing search (buyer search reached from a form redirect) */
    onSearchRedirect:Function=function(url:string){};
    /** Called with the site's thank-you page URL after a lead; return true when the page navigates there (no in-chat card). */
    onThankYouRedirect:Function=function(url:string){ return false; };
    feedbackURL=""; // non-empty shows thumbs up/down under each answer and posts the rating to this URL
}

export class AIChatBox {
    container:HTMLElement;
    root:ShadowRoot|Document;
    chatDivElement:HTMLDivElement;
    parser :MarkdownStreamParser;
    promptRunning=false;
    eventSource:EventSource|null=null;
    promptLimit=2000;
    lastSubmitTime=0;
    aiChatSessionId:string|null=null;
    totalTokensGenerated = 0;
    answerCount = 0; // completed answers in this chat, the next answer's 0-based index for feedback
    readonly AVG_RESPONSE_CHARACTERS = 1500;
    zidx:ZIDX;
    aiChatBoxConfig:AIChatBoxConfig;
    private thanksCard:HTMLElement|null=null;
    private hiddenByCard:HTMLElement|null=null;
    private confirmationEl:HTMLElement|null=null; // history bubble that holds only the lead confirmation line

    // Speech recognition
    private speechRecognition: any = null;
    private speechListening = false;

    constructor(zidx:ZIDX, aiChatBoxConfig:AIChatBoxConfig, root:ShadowRoot|Document, container:HTMLElement, callback:Function) {
        this.aiChatBoxConfig=aiChatBoxConfig;
        this.zidx=zidx;
        this.container=container;
        this.root=root;
        this.container.classList.add("aifcChatSetup");
        if(this.aiChatBoxConfig.initialSessionId){
            this.loadSessionHistory(this.aiChatBoxConfig.initialSessionId, callback);
        }else{
            this.initAiSession(callback);
        }

        let params = new URLSearchParams(window.location.search);
        let prompt = params.get("prompt");
        if (prompt) {
            // Replace URL-encoded '+' with spaces and decode standard %20 sequences
            prompt = decodeURIComponent(prompt.replace(/\+/g, " "));
        }

        // Restore pending prompt from cookie if returning from magic link/login redirect
        let savedPromptCookie = this.zidx.getCookie ? this.zidx.getCookie(LAST_PROMPT_COOKIE_KEY) : "";
        if (savedPromptCookie) {
            // Also clean up any escaped '+' signs from cookie strings
            prompt = decodeURIComponent(savedPromptCookie.replace(/\+/g, " "));

            // Clear the cookie once restored
            this.zidx.setCookie({
                key: LAST_PROMPT_COOKIE_KEY,
                value: "",
                futureSeconds: -1,
                enableSubdomains: true
            });
        }

        if (!prompt || this.aiChatBoxConfig.suppressUrlPrompt) {
            prompt = "";
        }

        let htmlInitial = '<div>\n' +
            '<div id="aiChatContainer">\n' +
            '\n' +
            '  <div class="aifcChatResponseBoxContainer idx-ai-thread" style="display:none;">\n' +
            '    <div class="aifcChatResponseBox"></div>\n' +
            '  </div>\n' +
            '\n' +
            '  <form class="aifcChatPromptForm idx-ai-composer">\n' +
            '    <div class="aifcChatPromptInputWrapper">\n' +
            '      <textarea\n' +
            '        name="aifcChatPromptInput"\n' +
            '        class="aifcChatPromptInput idx-ai-input"\n' +
            '        rows="1"\n '+
            '        placeholder="'+this.escapeAttr(this.aiChatBoxConfig.placeholder)+'"\n' +
            '      >'+prompt+'</textarea>\n' +
            '    <div class="aifcChatPromptActions">\n' +
            '      <button type="button" class="aifcChatMicBtn idx-ai-mic" title="Voice input" aria-label="Voice input">' +
            '        <svg class="aifcChatMicIcon" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
            '          <rect x="9" y="1" width="6" height="12" rx="3"/>' +
            '          <path d="M19 10v1a7 7 0 0 1-14 0v-1"/>' +
            '          <line x1="12" y1="19" x2="12" y2="23"/>' +
            '          <line x1="8" y1="23" x2="16" y2="23"/>' +
            '        </svg>' +
            '      </button>\n' +
            '      <button type="submit" class="aifcChatPromptSubmit idx-ai-send"><span class="idx-ai-send-label">Send</span>' +
            '<svg class="idx-ai-send-icon" xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h13M13 6l6 6-6 6"/></svg></button>\n' +
            '      <button type="button" class="aifcChatPromptStop idx-ai-stop" style="display:none;">Stop</button>\n' +
            '    </div>\n' +
            '    </div>\n' +
            '  </form>\n' +
            '\n' +
            '</div></div>';
        this.container.innerHTML=htmlInitial;
        let self=this;
        this.setSkinState("empty");
        this.chatDivElement=$(".aifcChatResponseBox", this.root)![0] as HTMLDivElement;
        this.parser=new MarkdownStreamParser(this.chatDivElement as HTMLElement);
        let promptInput=$(".aifcChatPromptInput", this.root)[0] as HTMLInputElement;
        promptInput.addEventListener('keydown', (event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                $(".aifcChatPromptForm", this.root).trigger("submit");
            }
        });

        const autoResize = () => {
            promptInput.style.height = "auto";
            promptInput.style.height = promptInput.scrollHeight + "px";
            // Switch to scrollable if we hit the max
            const max = Math.min(promptInput.scrollHeight, parseInt(getComputedStyle(promptInput).maxHeight, 10));
            promptInput.style.overflowY = promptInput.scrollHeight > max ? "auto" : "hidden";
        };

        promptInput.addEventListener("input", autoResize);

        // Run once on init in case the default value wraps
        autoResize();

        $(".aifcChatPromptStop", this.root).on("click", (e:JQuery.ClickEvent)=>{
            e.preventDefault();
            if(self.promptRunning) {
                if(self.eventSource!=null){
                    self.eventSource.close();
                    self.eventSource = null;
                }
                self.setProgressComplete();
                self.renderClientPrompt("Please stop generating the response.");
                self.parser.clear();
                self.parser.feed("\nOkay, what would you like help with now?");
                self.parser.complete();
                self.setPromptRunning(false);
            }
        });

        // Microphone button
        $(".aifcChatMicBtn", this.root).on("click", (e: JQuery.ClickEvent) => {
            e.preventDefault();
            self.toggleSpeechRecognition();
        });

        // Hide mic button if Speech API is completely unavailable
        if (!AIChatBox.isSpeechRecognitionSupported()) {
            $(".aifcChatMicBtn", this.root).hide();
        }

        $(".aifcChatPromptForm", this.root).on("submit", (e:JQuery.SubmitEvent)=>{
            e.preventDefault();
            if (self.promptRunning) {
                return;
            }
            // Stop any active speech recognition on submit
            if (self.speechListening) {
                this.speechListening = false;
                self.stopSpeechRecognition();
            }
            let prompt=promptInput.value.trim();
            if(prompt == ""){
                // self.parser.feed("Please enter a prompt before making your submission.");
                self.parser.complete();
                return;
            }

            // Check AI prompt registration gate before running any session checks or API calls
            if (self.checkAIPromptGate(prompt)) {
                return;
            }

            if(!self.aiChatSessionId){
                self.parser.feed("Initializing session, please try again in a moment.");
                self.parser.complete();
                return;
            }
            if(prompt.length>self.promptLimit){
                self.parser.feed("Your prompt is too long. Please reduce it to "+self.promptLimit+" or less characters.");
                self.parser.complete();
                promptInput.value=prompt.substring(0, self.promptLimit);
                return;
            }
            prompt=self.aiChatBoxConfig.onPromptCallback(prompt);
            const now = Date.now();
            if (now - self.lastSubmitTime < 1000) {
                return;
            }
            self.lastSubmitTime = now;

            promptInput.value="";
            self.setSkinState("active");
            self.renderClientPrompt(prompt);
            self.setPromptRunning(true);
            try {
                self.startEventSource(prompt);
            } catch (err) {
                console.error("Failed to start chat stream:", err);
                self.setPromptRunning(false);
            }

        });

        if(prompt!=""){
            setTimeout(function(){
                $(".aifcChatPromptForm", self.root).trigger("submit");
            }, 100);
        }
    }

    /** Sends text as the visitor's next message, exactly as if they typed it and pressed Send. */
    sendPrompt(text: string) {
        const input = $(".aifcChatPromptInput", this.root)[0] as HTMLTextAreaElement | undefined;
        if (!input) return;
        input.value = text;
        $(".aifcChatPromptForm", this.root).trigger("submit");
    }

    // ── Speech Recognition ──────────────────────────────────────────────

    static isSpeechRecognitionSupported(): boolean {
        return !!(window as any).SpeechRecognition || !!(window as any).webkitSpeechRecognition;
    }

    private toggleSpeechRecognition() {
        if (this.speechListening) {
            this.stopSpeechRecognition();
        } else {
            this.startSpeechRecognition();
        }
    }

    private startSpeechRecognition() {
        if (!AIChatBox.isSpeechRecognitionSupported()) {
            this.showSpeechError("Speech recognition is not supported in this browser.");
            return;
        }

        const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
        const recognition = new SpeechRecognition();

        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = "en-US";

        const promptInput = $(".aifcChatPromptInput", this.root)[0] as HTMLInputElement;
        const initialText = promptInput.value;
        let sessionFinalTranscript = "";

        recognition.onstart = () => {
            this.speechListening = true;
            this.setSpeechActiveState(true);
        };

        recognition.onresult = (event: any) => {
            let interimTranscript = "";
            for (let i = event.resultIndex; i < event.results.length; i++) {
                const transcript = event.results[i][0].transcript;
                if (event.results[i].isFinal) {
                    sessionFinalTranscript += transcript;
                } else {
                    interimTranscript += transcript;
                }
            }

            const separator = initialText.length > 0 ? " " : "";
            promptInput.value = initialText + separator + sessionFinalTranscript + interimTranscript;
            promptInput.dispatchEvent(new Event('input'));
        };

        recognition.onerror = (event: any) => {
            if (event.error === "no-speech") return;
            console.error("Speech Error:", event.error);
            this.stopSpeechRecognition();
        };

        recognition.onend = () => {
            if (this.speechListening) {
                try {
                    this.speechRecognition.start();
                } catch (e) {
                    this.setSpeechActiveState(false);
                    this.speechListening = false;
                }
            } else {
                this.setSpeechActiveState(false);
            }
        };

        recognition.start();
        this.speechRecognition = recognition;
    }

    private stopSpeechRecognition() {
        if (this.speechRecognition) {
            this.speechRecognition.stop();
            this.speechRecognition = null;
            this.speechListening = false;
            this.setSpeechActiveState(false);
        }
    }

    private setSpeechActiveState(active: boolean) {
        const $btn = $(".aifcChatMicBtn", this.root);
        if (active) {
            $btn.addClass("aifcChatMicListening");
        } else {
            $btn.removeClass("aifcChatMicListening");
        }
    }

    private showSpeechError(message: string) {
        const existing = $(".aifcChatSpeechError", this.root);
        if (existing.length) existing.remove();

        const errorDiv = document.createElement("div");
        errorDiv.className = "aifcChatSpeechError";
        errorDiv.textContent = message;
        $(".aifcChatPromptForm", this.root).after(errorDiv);

        setTimeout(() => {
            $(errorDiv).fadeOut(300, () => $(errorDiv).remove());
        }, 5000);
    }

    // ── Session Handling ────────────────────────────────────────────────

    async initAiSession(callback:Function): Promise<void> {
        try {
            const params = new URLSearchParams();
            params.append("endpoint", this.aiChatBoxConfig.aiEndPointURL);
            params.append("aiBotId", String(this.aiChatBoxConfig.aiBotId));
            const res = await fetch(this.zidx.options.selectedSiteDomain+"/new-ai-chat/", {
                method: "POST",
                credentials: "include",
                body: params,
            });
            const data = await res.json();
            if (data.success && data.data) {
                this.aiChatSessionId = data.data;
                this.aiChatBoxConfig.onSessionReady(this.aiChatSessionId);
                callback();
            } else {
                console.error("Failed to get AI session ID", data);
            }
        } catch (e) {
            console.error("Failed to init AI search session", e);
        }
    }

    private escapeAttr(value: string): string {
        return value.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    }

    /**
     * Skin hook: data-idx-ai-state="empty|active" is set on the .idx-ai-root element when a custom skin is
     * active (or on the chat container otherwise). Custom skins can restyle each state.
     */
    private setSkinState(state: "empty" | "active") {
        const skinRoot = this.container.closest(".idx-ai-root") as HTMLElement | null;
        (skinRoot || this.container).setAttribute("data-idx-ai-state", state);
        if (state === "active" && this.aiChatBoxConfig.clearPlaceholderAfterFirstReply) {
            const input = this.container.querySelector(".aifcChatPromptInput") as HTMLTextAreaElement | null;
            if (input) input.placeholder = "";
        }
    }

    /**
     * Creates the element a streamed or restored answer is rendered into. With overviewLabel set the answer
     * is wrapped in an "AI Overview" block (idx-ai-overview) with a label and a body.
     */
    private createAnswerContainer(): HTMLElement {
        const body = document.createElement("div");
        body.className = "aifcChatResponseItem idx-ai-answer";
        if (!this.aiChatBoxConfig.overviewLabel) {
            this.chatDivElement.appendChild(body);
            return body;
        }
        body.classList.add("idx-ai-overview-body");
        const wrapper = document.createElement("div");
        wrapper.className = "idx-ai-overview";
        const label = document.createElement("div");
        label.className = "idx-ai-overview-label";
        label.innerHTML = '<svg class="idx-ai-overview-icon" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2l2.2 6.3L20.5 10l-6.3 1.7L12 18l-2.2-6.3L3.5 10l6.3-1.7L12 2zm7 12l1 2.7 2.7 1-2.7 1L19 21.4l-1-2.7-2.7-1 2.7-1L19 14z"/></svg><span class="idx-ai-overview-label-text"></span>';
        (label.querySelector(".idx-ai-overview-label-text") as HTMLElement).textContent = this.aiChatBoxConfig.overviewLabel;
        wrapper.appendChild(label);
        wrapper.appendChild(body);
        this.chatDivElement.appendChild(wrapper);
        if (this.aiChatBoxConfig.overviewCollapse) {
            this.setupOverviewCollapse(body);
        }
        return body;
    }

    /**
     * Collapses the overview to 20 lines and adds a Read more / Show less button as soon as the text grows
     * past 20 lines, including while the answer is still streaming. A MutationObserver runs before the browser
     * paints, so the extra lines never appear. Line height is measured from the rendered text, so with headings
     * and lists the cutoff is approximate.
     */
    private setupOverviewCollapse(body: HTMLElement) {
        const wrapper = body.parentElement as HTMLElement;
        let button: HTMLButtonElement | null = null;
        const check = () => {
            if (button) return; // already collapsed once; from then on the visitor controls it
            const cs = getComputedStyle(body);
            let lineHeight = parseFloat(cs.lineHeight);
            if (isNaN(lineHeight)) lineHeight = parseFloat(cs.fontSize) * 1.4;
            const maxHeight = Math.round(lineHeight * 20);
            if (body.scrollHeight <= maxHeight + 6) return;
            body.classList.add("idx-ai-overview-collapsed");
            body.style.maxHeight = maxHeight + "px";
            button = document.createElement("button");
            button.type = "button";
            button.className = "idx-ai-readmore";
            button.textContent = "Read more";
            button.setAttribute("aria-expanded", "false");
            const toggleButton = button;
            toggleButton.addEventListener("click", (e) => {
                e.preventDefault();
                const collapsed = body.classList.toggle("idx-ai-overview-collapsed");
                body.style.maxHeight = collapsed ? maxHeight + "px" : "none";
                toggleButton.textContent = collapsed ? "Read more" : "Show less";
                toggleButton.setAttribute("aria-expanded", collapsed ? "false" : "true");
            });
            wrapper.appendChild(toggleButton);
        };
        const observer = new MutationObserver(check);
        observer.observe(body, {childList: true, subtree: true, characterData: true});
        (body as any).idxAiOverview = {check, observer};
    }

    /** Called when an answer is complete: one last check, then stop observing. */
    private collapseOverview(body: HTMLElement) {
        const state = (body as any).idxAiOverview;
        if (!state) return;
        requestAnimationFrame(() => {
            state.check();
            state.observer.disconnect();
            (body as any).idxAiOverview = null;
        });
    }

    renderClientPrompt(prompt: string) {
        let container = document.createElement("div");
        container.className = "aifcChatPromptItemContainer idx-ai-question-row";

        let div = document.createElement("div");
        div.className = "aifcChatPromptItemBox idx-ai-question";
        div.textContent = prompt;
        container.appendChild(div);

        let loader = document.createElement("div");
        loader.className = "aifcChatLoadingDots idx-ai-loading";
        loader.id = "aifcCurrentLoader";
        loader.innerHTML = '<span class="dot1"></span><span class="dot2"></span><span class="dot3"></span>';

        this.chatDivElement.appendChild(container);
        this.chatDivElement.appendChild(loader);
    }

    setPromptRunning(running:boolean){
        this.promptRunning=running;
        let self=this;
        if(running){
            $(".aifcChatResponseBoxContainer", this.root).show();
            setTimeout(function(){
                let prompts = self.chatDivElement.querySelectorAll(".aifcChatPromptItemContainer");
                let target = prompts.length ? prompts[prompts.length - 1] as HTMLElement : null;
                // embedded skinned chat: the header is fixed at the top, so never scroll for the first prompt
                // (the page is already at the top) and leave room for the header on later prompts
                const embedRoot = self.container.closest(".idx-ai-embed");
                if (embedRoot && prompts.length <= 1) {
                    target = null;
                }
                if (target) {
                    const stickyHeader = embedRoot ? embedRoot.querySelector(".idx-ai-header") as HTMLElement | null : null;
                    target.style.scrollMarginTop = stickyHeader ? stickyHeader.offsetHeight + "px" : "";
                    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }, 10);
            $(".aifcChatPromptSubmit", this.root).hide();
            $(".aifcChatPromptStop", this.root).show();
        }else{
            $(".aifcChatPromptSubmit", this.root).show();
            $(".aifcChatPromptStop", this.root).hide();
        }
    }

    testMarkdownStream() {
        let self=this;
        let characterByCharacter=true;
        this.setPromptRunning(true);
        const chunks = [
            '# Heading 1\n',
            '## Heading 2\n',
            '### Heading 3\n',
            '#### Heading 4\n',
            '##### Heading 5\n',
            '###### Heading 6\n',
            'This is an example ',
            'of a paragraph with ',
            'some content that is ',
            'broken into multiple chunks to simulate a stream.\n',
            '### Tables\n',
            '| Feature | Status |\n',
            '|---|---|\n',
            '| Tables |',
            ' Supported |\n',
            '| Headings | Supported |\n',
            '| Lists | Supported |\n\n',
            '### Horizontal Rules\n' +
            '---\n' +
            '***\n' +
            '___\n' +
            '- - -\n',
            '### Blockquotes\n',
            '> This is a blockquote.\n',
            '> It can span multiple',
            ' lines.\n\n',
            'This paragraph contains some **bold** text, some *italic* text, and some ***bold and italic*** text. **[bold link](https://example.com)** also works.\n',
            'It also has a strikethrough like ~~this~~.',
            ' It also includes `inline code` and a [link](https://example.com).\n\n',
            'Here are some different lists:\n\n',
            '### Unordered List\n',
            '- Item one\n',
            '- Item two\n',
            '    - Nested item A\n',
            '    - Nested item B\n',
            '- Item three\n\n',
            '### Ordered List\n',
            '1. First item\n',
            '2. Second item\n',
            '    1. Nested ordered item\n',
            '    2. Another one\n',
            '3. Third item\n\n',
            '### Task List\n',
            '- [x] Task one (completed)\n',
            '- [ ] Task two (not yet)\n\n',
            '### Code\n',
            '```javascript\n',
            'function helloWorld() {\n',
            '  console.log("Hello, World!");\n',
            '}\n',
            '```\n\n'
        ];

        if(characterByCharacter) {
            let i = 0;
            const interval = setInterval(() => {
                if (i < chunks.length) {
                    self.parser.feed(chunks[i]);
                    i++;
                } else {
                    clearInterval(interval);
                    self.parser.complete();
                    self.setPromptRunning(false);
                }
            }, 100);

        }else{
            for(let i = 0;i<chunks.length;i++) {
                this.parser.feed(chunks[i]);
            }
            this.parser.complete();
            this.setPromptRunning(false);
        }

        const htmlStrings = this.parser.getGeneratedHtmlStrings();
        console.log("HTML Output:");
        console.log(htmlStrings.join("\n"));
    }

    updateProgressBar(tokens: number) {
        this.totalTokensGenerated += tokens;
        const percentage = Math.min(Math.round((this.totalTokensGenerated / this.AVG_RESPONSE_CHARACTERS) * 100), 100);

        const $bar = $(".aifcChatProgressBar", this.root);
        $bar.css("width", percentage + "%");

        if (percentage >= 100) {
            $bar.css("background", "#4CAF50");
        }
    }

    resetProgressBar() {
        this.totalTokensGenerated = 0;
        $(".aifcChatLoadingMessage", this.zidx.peakidxShadowRoot).remove();
        $(".aifcChatProgressContainer", this.zidx.peakidxShadowRoot).remove();
        $(".aifcChatResponseBox", this.zidx.peakidxShadowRoot).append("<p class=\"aifcChatLoadingMessage\">Loading response...</p>");
        $(".aifcChatResponseBox", this.zidx.peakidxShadowRoot).append('<div class="aifcChatProgressContainer" style="display:none; width: 100%; background: #eee; height: 10px; border-radius: 5px; margin-bottom: 10px; overflow: hidden;">\n' +
            '    <div class="aifcChatProgressBar" style="width: 0%; height: 100%; background: #2196F3; transition: width 0.3s ease;"></div>\n' +
            '  </div>\n');
        $(".aifcChatProgressBar", this.root).css({"width": "0%", "background": "#369"});
        $(".aifcChatProgressContainer", this.root).show();
    }

    async loadSessionHistory(sessionId:string, callback:Function): Promise<void> {
        try {
            const params = new URLSearchParams();
            params.append("endpoint", this.aiChatBoxConfig.aiEndPointURL);
            const res = await fetch(this.zidx.options.selectedSiteDomain+"/resume-ai-chat/?sessionId=" + encodeURIComponent(sessionId), {
                method: "POST",
                credentials: "include",
            });
            const data = await res.json();
            if (data.success && data.messages) {
                this.aiChatSessionId = data.sessionId;
                this.renderHistoryMessages(data.messages);
                if (data.completion && this.aiChatBoxConfig.initialThanks) {
                    this.showThanks(data.completion as LeadCompletePayload, this.confirmationEl);
                }
                this.aiChatBoxConfig.onSessionReady(this.aiChatSessionId);
                this.aiChatBoxConfig.onHistoryLoaded(data.messages);
            } else {
                this.initAiSession(callback);
            }
        } catch (e) {
            console.error("Failed to load session history", e);
            this.initAiSession(callback);
        }
    }

    renderHistoryMessages(messages:any[]){
        if(messages.length === 0) return;
        let promptInput=$(".aifcChatPromptInput", this.root)[0] as HTMLInputElement;
        if(promptInput) promptInput.value="";
        $(".aifcChatResponseBoxContainer", this.root).show();
        this.setSkinState("active");
        for(let msg of messages){
            if(msg.role === "user"){
                this.renderClientPrompt(msg.content || "");
            }else if(msg.role === "assistant" && msg.content){
                let container = this.createAnswerContainer();
                let historyParser = new MarkdownStreamParser(container);
                historyParser.feed(msg.content);
                historyParser.complete();
                this.collapseOverview(container);
                this.addFeedbackButtons(container, this.answerCount++, msg.feedback || 0);
                if (msg.completionConfirmation) this.confirmationEl = (container.closest(".idx-ai-overview") as HTMLElement) || container;
            }
        }
        this.parser = new MarkdownStreamParser(this.chatDivElement as HTMLElement);
        let lastChild = this.chatDivElement.lastElementChild;
        if(lastChild){
            setTimeout(()=>{
                lastChild!.scrollIntoView({ behavior: 'smooth', block: 'end' });
            }, 50);
        }
    }

    startEventSource(prompt: string) {
        let self = this;

        if (this.eventSource) {
            this.eventSource.close();
            this.eventSource = null;
        }

        let responseContainer = this.createAnswerContainer();

        const parser = new MarkdownStreamParser(responseContainer);
        this.parser = parser;

        let url = this.zidx.options.selectedSiteDomain+this.aiChatBoxConfig.aiEndPointURL + "?user_input=" + encodeURIComponent(prompt)
            + "&ai_chat_session_id=" + encodeURIComponent(self.aiChatSessionId || "")
            + this.aiChatBoxConfig.extraQueryParams;

        let eventSource = new EventSource(url, {
            withCredentials: true,
        });
        this.eventSource = eventSource;
        const isCurrent = () => self.eventSource === eventSource;

        const SSE_DELIMITER = '\u0010';
        const decodeSSE = (raw: string): string => raw.replaceAll(SSE_DELIMITER, "\n");

        eventSource.onmessage = (event) => {
            if (!isCurrent()) return;
            const loader = self.root.querySelector("#aifcCurrentLoader");
            if (loader) {
                loader.remove();
            }

            let decoded = decodeSSE(event.data);
            let arrLines = decoded.split("\n");
            for (let i = 0; i < arrLines.length; i++) {
                arrLines[i] = this.aiChatBoxConfig.onText(arrLines[i]);
            }
            parser.feed(arrLines.join("\n"));
        };

        eventSource.addEventListener('idxresults', (event: any) => {
            let data = decodeSSE(event.data);
            if (data) {
                try {
                    let parsed = JSON.parse(data);
                    self.aiChatBoxConfig.onJSON(parsed);
                } catch (e) {
                    self.aiChatBoxConfig.onJSON(data);
                }
            }
        });

        eventSource.addEventListener('idxtokens', (event: any) => {
            let data = decodeSSE(event.data);
            if (data) {
                let count = parseInt(data.trim(), 10);
                if (!isNaN(count)) {
                    self.aiChatBoxConfig.onTokens(count);
                }
            }
        });

        eventSource.addEventListener('xml', (event: any) => {
            if (event.data) {
                self.aiChatBoxConfig.onXML(decodeSSE(event.data));
            }
        });

        eventSource.addEventListener('searchquery', (event: any) => {
            const query = decodeSSE(event.data || "").trim();
            if (query) {
                self.aiChatBoxConfig.onSearchQuery(query);
            }
        });

        eventSource.addEventListener('leadcomplete', (event: any) => {
            try {
                const payload = JSON.parse(decodeSSE(event.data || "")) as LeadCompletePayload;
                if (payload.thankYouUrl && self.aiChatBoxConfig.onThankYouRedirect(payload.thankYouUrl)) return;
                // the chat bubble of this turn only repeats the confirmation line that the card contains
                const bubble = (responseContainer.closest(".idx-ai-overview") as HTMLElement) || responseContainer;
                self.showThanks(payload, payload.confirmationOnly ? bubble : null);
            } catch (e) {
                console.error("Bad leadcomplete event", e);
            }
        });

        eventSource.addEventListener('searchredirect', (event: any) => {
            try {
                const url = String(JSON.parse(decodeSSE(event.data || "")).url || "");
                if (url) self.aiChatBoxConfig.onSearchRedirect(url);
            } catch (e) {
                console.error("Bad searchredirect event", e);
            }
        });

        eventSource.addEventListener('close', () => {
            if (!isCurrent()) return;
            self.collapseOverview(responseContainer);
            self.addFeedbackButtons(responseContainer, self.answerCount++, 0);
            eventSource.close();
            self.setPromptRunning(false);
            self.setProgressComplete();
            self.aiChatBoxConfig.onDataCompleteCallback();
        });

        eventSource.onerror = (event) => {
            if (!isCurrent()) return;
            const loader = self.root.querySelector("#aifcCurrentLoader");
            if (loader) loader.remove();

            parser.complete();
            parser.feed("Sorry, there was an error. Please try again later.");
            parser.complete();
            console.error("EventSource failed:", event);
            eventSource.close();
            self.setPromptRunning(false);
            self.setProgressComplete();
            self.collapseOverview(responseContainer);
        };

    }

    /**
     * Thank-you view: the card replaces the input area (the prompt form is hidden, not removed, and the chat session is
     * untouched). "Back to chat" restores the form and the confirmation bubble on the same session.
     */
    showThanks(payload: LeadCompletePayload, hideEl: HTMLElement | null) {
        this.removeThanksCard();
        const form = this.container.querySelector(".aifcChatPromptForm") as HTMLElement | null;
        if (!form) return;
        this.hiddenByCard = hideEl;
        if (hideEl) hideEl.classList.add("idx-ai-hidden-by-card");
        form.classList.add("idx-ai-composer-hidden");
        this.container.classList.add("idx-ai-thanks-active");
        const card = buildLeadCompleteCard(payload, () => this.hideThanks(), this.aiChatBoxConfig.onNewChat);
        this.thanksCard = card;
        form.parentElement!.insertBefore(card, form);
        $(".aifcChatResponseBoxContainer", this.root).show();
        this.setSkinState("active");
        setTimeout(() => card.scrollIntoView({behavior: "smooth", block: "nearest"}), 50);
        this.aiChatBoxConfig.onThanksShown();
    }

    hideThanks() {
        this.removeThanksCard();
        const form = this.container.querySelector(".aifcChatPromptForm") as HTMLElement | null;
        if (form) form.classList.remove("idx-ai-composer-hidden");
        this.container.classList.remove("idx-ai-thanks-active");
        if (this.hiddenByCard) this.hiddenByCard.classList.remove("idx-ai-hidden-by-card");
        this.hiddenByCard = null;
        const input = this.container.querySelector(".aifcChatPromptInput") as HTMLTextAreaElement | null;
        if (input) input.focus();
        this.aiChatBoxConfig.onThanksHidden();
    }

    private removeThanksCard() {
        if (this.thanksCard) this.thanksCard.remove();
        this.thanksCard = null;
    }

    /** Thumbs up/down under a finished answer. Clicking the selected thumb again clears the rating. */
    private addFeedbackButtons(answer: HTMLElement, answerIndex: number, rating: number) {
        const url = this.aiChatBoxConfig.feedbackURL;
        if (!url || !this.aiChatSessionId) return;
        const sessionId = this.aiChatSessionId;
        const bar = document.createElement("div");
        bar.className = "idx-ai-feedback";
        let current = rating;
        const buttons: {[key: number]: HTMLButtonElement} = {};
        const refresh = () => {
            for (const value of [1, -1]) {
                buttons[value].classList.toggle("idx-ai-feedback-selected", current === value);
                buttons[value].setAttribute("aria-pressed", current === value ? "true" : "false");
            }
        };
        for (const value of [1, -1]) {
            const button = document.createElement("button");
            button.type = "button";
            button.className = "idx-ai-feedback-button";
            button.title = value === 1 ? "Good answer" : "Bad answer";
            button.setAttribute("aria-label", button.title);
            button.textContent = value === 1 ? "👍" : "👎";
            button.addEventListener("click", () => {
                const next = current === value ? 0 : value;
                const params = new URLSearchParams();
                params.append("ai_chat_session_id", sessionId);
                params.append("answer_index", String(answerIndex));
                params.append("rating", String(next));
                fetch(this.zidx.options.selectedSiteDomain + url, {method: "POST", credentials: "include", body: params})
                    .then(() => { current = next; refresh(); })
                    .catch((e) => console.error("Failed to save feedback", e));
            });
            buttons[value] = button;
            bar.appendChild(button);
        }
        refresh();
        answer.appendChild(bar);
    }

    setProgressComplete(){
        $(".aifcChatLoadingMessage", this.zidx.peakidxShadowRoot).hide();
        $(".aifcChatProgressContainer", this.zidx.peakidxShadowRoot).hide();
    }

    // ── Gating & Limits ─────────────────────────────────────────────────

    private checkAIPromptGate(prompt: string): boolean {
        // Skip gate if logged in as a real non-guest user
        if (this.zidx.options.aiChatMessageLimit==0){
            return false;
        }
        if (this.zidx.isContactLoggedIn() && !this.zidx.isContactGuest()) return false;

        const count = this.incrementAIPromptCount();

        // Trigger threshold on or after the X prompt
        if (this.zidx.options.aiChatMessageLimit!= 0 && count >= this.zidx.options.aiChatMessageLimit) {
            // Store their current input message in a subdomain-enabled cookie (expires in 1 day)
            this.zidx.setCookie({
                key: LAST_PROMPT_COOKIE_KEY,
                value: prompt,
                futureSeconds: 60 * 60 * 24,
                enableSubdomains: true
            });

            // Trigger modal
            this.zidx.showLoginForm();
            return true;
        }
        return false;
    }

    private getAIPromptCount(): ViewRecord | null {
        try {
            const raw = localStorage.getItem(AI_PROMPT_STORAGE_KEY);
            if (!raw) return null;
            const record: ViewRecord = JSON.parse(raw);
            const ageMs = Date.now() - record.firstSeen;
            if (ageMs > AI_PROMPT_EXPIRY_DAYS * 86400 * 1000) {
                localStorage.removeItem(AI_PROMPT_STORAGE_KEY);
                return null;
            }
            return record;
        } catch {
            return null;
        }
    }

    private incrementAIPromptCount(): number {
        const existing = this.getAIPromptCount();
        const record: ViewRecord = existing ?? { count: 0, firstSeen: Date.now() };
        record.count += 1;
        try {
            localStorage.setItem(AI_PROMPT_STORAGE_KEY, JSON.stringify(record));
        } catch {
            // Graceful fallback for full/blocked local storage
        }
        return record.count;
    }

    static resetAIPromptCount() {
        localStorage.removeItem(AI_PROMPT_STORAGE_KEY);
    }
}